import { useRef, useState } from 'react';
import { Share2, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { useToast } from '@/hooks/use-toast';
import LeaderboardCapture from './LeaderboardCapture';
import { trackEvent } from '@/lib/analytics';

interface LeaderboardEntry {
  id: string;
  user_id: string | null;
  display_name: string | null;
  total_points: number | null;
  correct_winners: number | null;
  correct_scores: number | null;
  rank: number | null;
}

interface LeaderboardShareButtonProps {
  entries: LeaderboardEntry[];
  groupName: string;
}

const LeaderboardShareButton = ({ entries, groupName }: LeaderboardShareButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const captureRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const handleShare = async () => {
    if (!captureRef.current) return;

    setIsLoading(true);
    try {
      const dataUrl = await toPng(captureRef.current, {
        pixelRatio: 2,
        backgroundColor: 'hsl(0, 0%, 5%)',
      });

      // Convert data URL to blob for Web Share API
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'bracket-run-leaderboard.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${groupName} — דירוג`,
          text: 'דירוג הקבוצה ב-BRACKET RUN',
        });
        trackEvent('leaderboard_viewed');
      } else {
        // Desktop fallback: download
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'bracket-run-leaderboard.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        trackEvent('leaderboard_viewed');
        toast({
          title: 'התמונה הורדה בהצלחה',
          description: 'שתף את התמונה בקבוצת הוואטסאפ!',
        });
      }
    } catch (error) {
      // AbortError means user cancelled the share sheet — not an error
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error('Share failed:', error);
      toast({
        title: 'השיתוף נכשל',
        description: 'נסה שוב מאוחר יותר',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleShare}
        disabled={isLoading}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border bg-card hover:bg-accent/10 transition-colors disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Share2 className="w-4 h-4" />
        )}
        שתף דירוג
      </button>

      <LeaderboardCapture
        ref={captureRef}
        entries={entries.slice(0, 10)}
        groupName={groupName}
      />
    </>
  );
};

export default LeaderboardShareButton;
